import { z } from "zod";

export const SolicitacaoStatusEnum = z.enum([
  "analysis",
  "requested",
  "accepted",
  "delivering",
  "delivered",
  "canceled",
  "rejected",
]);

export const IdentifySolicitacaoRequestSchema = z.object({
  phoneLast4: z.string().length(4),
});

export const IdentifySolicitacaoResponseSchema = z.object({
  token: z.string(),
  participant: z.record(z.string(), z.any()),
});

export const FormaPagamentoEnum = z.enum([
  "pix",
  "credito",
  "debito",
  "saldo",
]);

export const SolicitacaoTypeEnum = z.enum(["send", "receive"]);

export const AddressPointSchema = z.object({
  name: z.string(),
  phone: z.string(),
  address: z.string(),
  latitude: z.number(),
  longitude: z.number(),
  estado: z.string(),
  cidade: z.string(),
  logradouro: z.string(),
  bairro: z.string(),
  numero: z.string(),
  referencia: z.string().optional(),
});

export const EstimateAddressPointSchema = z.object({
  nome: z.string(),
  telefone: z.string(),
  address: z.string(),
  latitude: z.number(),
  longitude: z.number(),
  estado: z.string(),
  cidade: z.string(),
  logradouro: z.string(),
  bairro: z.string(),
  numero: z.string(),
  referencia: z.string().optional(),
});

export const EstimateSolicitacaoSchema = z.object({
  origin: EstimateAddressPointSchema,
  destination: EstimateAddressPointSchema,
  sendPOS: z.boolean().optional(),
  observacao: z.string().optional(),
  type: SolicitacaoTypeEnum,
});

export const EstimateSolicitacaoResponseSchema = z.object({
  valor: z.number(),
  estimate_id: z.string(),
  distancia_km: z.number(),
});

export const CreateSolicitacaoSchema = z.object({
  type: SolicitacaoTypeEnum,

  // Origem: pode ser useCurrentLocation, favorito, snapshot ou objeto
  useCurrentLocation: z.boolean().optional(),
  originFavoritoId: z.number().optional(),
  originId: z.number().optional(),
  origin: AddressPointSchema.optional(),
  saveOriginAsApelido: z.string().optional(),

  // Destino: pode ser favorito, snapshot ou objeto
  destinationFavoritoId: z.number().optional(),
  destinationId: z.number().optional(),
  destination: AddressPointSchema.optional(),
  saveDestinationAsApelido: z.string().optional(),

  sendPOS: z.boolean(),
  estimateId: z.string(),
  formaPagamento: FormaPagamentoEnum,
  valor: z.number().positive(),
});

export const UpdateSolicitacaoSchema = z.object({
  status: SolicitacaoStatusEnum.optional(),
  valor: z.number().optional(),
  formaPagamento: FormaPagamentoEnum.optional(),
});

export const EnderecoSnapshotSchema = z.object({
  id: z.number(),
  userId: z.number(),
  nome: z.string(),
  telefone: z.string(),
  latitude: z.string(),
  longitude: z.string(),
  estado: z.string(),
  cidade: z.string(),
  logradouro: z.string(),
  bairro: z.string(),
  numero: z.string(),
  referencia: z.string().nullable().optional(),
  createdAt: z.string().datetime(),
});

const StatusMap: Record<string, string> = {
  'analysis': 'analysis',
  'requested': 'requested',
  'accepted': 'accepted',
  'delivering': 'delivering',
  'delivered': 'delivered',
  'canceled': 'canceled',
  'rejected': 'rejected',
};

const PaymentMap: Record<string, string> = {
  '5': 'credito',
  '14': 'debito',
  '28': 'pix',
  '100': 'saldo',
  'Pix': 'pix',
  'credito': 'credito',
  'debito': 'debito',
  'pix': 'pix',
  'saldo': 'saldo',
};

export const SolicitacaoResponseSchema = z.object({
  id: z.number(),
  userId: z.number(),
  tipo: SolicitacaoTypeEnum.optional().default("send"),
  externalId: z.preprocess((val) => Number(val), z.number()),
  status: z.preprocess((val) => StatusMap[String(val)] || val, SolicitacaoStatusEnum),
  valor_entrega: z.string(),
  formaPagamento: z.preprocess((val) => PaymentMap[String(val)] || val, FormaPagamentoEnum),
  deliveryChannel: z.string().nullable().optional(),
  updateChannel: z.string().nullable().optional(),
  origemSnapshot: EnderecoSnapshotSchema,
  destinoSnapshot: EnderecoSnapshotSchema,
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
});

export const SolicitacaoCreateResponseSchema = z.object({
  id: z.number(),
});

export const SolicitacaoCountResponseSchema = z.object({
  count: z.number(),
});

export type SolicitacaoStatus = z.infer<typeof SolicitacaoStatusEnum>;
export type FormaPagamento = z.infer<typeof FormaPagamentoEnum>;
export type SolicitacaoType = z.infer<typeof SolicitacaoTypeEnum>;
export type AddressPointDto = z.infer<typeof AddressPointSchema>;
export type EstimateAddressPointDto = z.infer<typeof EstimateAddressPointSchema>;
export type CreateSolicitacaoDto = z.infer<typeof CreateSolicitacaoSchema>;
export type UpdateSolicitacaoDto = z.infer<typeof UpdateSolicitacaoSchema>;
export type EnderecoSnapshotDto = z.infer<typeof EnderecoSnapshotSchema>;
export type SolicitacaoResponseDto = z.infer<typeof SolicitacaoResponseSchema>;
export type SolicitacaoCreateResponseDto = z.infer<
  typeof SolicitacaoCreateResponseSchema
>;
export type SolicitacaoCountResponseDto = z.infer<
  typeof SolicitacaoCountResponseSchema
>;
export type EstimateSolicitacaoDto = z.infer<typeof EstimateSolicitacaoSchema>;
export type EstimateSolicitacaoResponseDto = z.infer<
  typeof EstimateSolicitacaoResponseSchema
>;
export type IdentifySolicitacaoRequestDto = z.infer<
  typeof IdentifySolicitacaoRequestSchema
>;
export type IdentifySolicitacaoResponseDto = z.infer<
  typeof IdentifySolicitacaoResponseSchema
>;
